import { ReactNode, useMemo } from "react";
import ReactSelect, {
  Styles,
  components,
  OptionProps,
  SingleValueProps,
} from "react-select";
import { Box, Flex, Text } from "@chakra-ui/react";
import { ReactComponentLike } from "prop-types";
import { FiChevronDown } from "react-icons/fi";
import { theme } from "@ovou-inc/ovou-ui";
import { Icon } from "components";

import {
  hiddenIndicatorSeparator,
  selectMenuBase,
  selectMenuListBase,
  selectMenuPortalBase,
  selectOptionBase,
  selectPlaceholderBase,
  underlineControlBase,
} from "./selectStyles";

export type DropdownValue = string | number;

export interface DropdownOption<T extends DropdownValue = DropdownValue> {
  value: T;
  label: string;
  rightAdornment?: ReactNode;
}

export interface DropdownProps<T extends DropdownValue = DropdownValue> {
  options: DropdownOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  ariaLabel?: string;
}

const SelectedCheck = () => (
  <Box
    as="span"
    ml="auto"
    pl="8px"
    sx={{
      svg: {
        color: theme.palette.brand.primary.orange,
        height: "1rem",
        width: "1rem",
      },
    }}
  >
    <Icon name="check" />
  </Box>
);

const Option = (props: OptionProps<DropdownOption, false>) => {
  const OptionComponent = components.Option as ReactComponentLike;
  return (
    <OptionComponent {...props}>
      <Flex alignItems="center" width="100%">
        <Text
          as="span"
          color={
            props.isSelected
              ? theme.palette.brand.primary.orange
              : theme.palette.brand.primary.black
          }
        >
          {props.data.label}
        </Text>
        {props.data.rightAdornment}
        {props.isSelected && <SelectedCheck />}
      </Flex>
    </OptionComponent>
  );
};

const SingleValue = (props: SingleValueProps<DropdownOption>) => {
  const SingleValueComponent = components.SingleValue as ReactComponentLike;
  return (
    <SingleValueComponent {...props}>
      <Flex alignItems="center">
        <Text as="span" color={theme.palette.brand.primary.black}>
          {props.data.label}
        </Text>
        {props.data.rightAdornment}
      </Flex>
    </SingleValueComponent>
  );
};

// Figma "Dropdown s" uses a clean 16px chevron_big_down; react-select's
// built-in indicator is a chunkier glyph. Swap in the Feather chevron (already
// the app's chevron, used in the search fields) and let the wrapper's
// `dropdownIndicator` style handle the open/close rotation.
const DropdownIndicator = (props: {
  selectProps: { menuIsOpen?: boolean };
}) => {
  const IndicatorComponent = components.DropdownIndicator as ReactComponentLike;
  return (
    <IndicatorComponent {...props}>
      <FiChevronDown size={16} color={theme.palette.brand.primary.black} />
    </IndicatorComponent>
  );
};

const dropdownStyles: Styles<DropdownOption, false> = {
  control: (base, state) => ({
    ...underlineControlBase(base),
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    opacity: state.isDisabled ? 0.6 : 1,
    "&:hover": {
      borderBottom: `1px solid ${theme.palette.ui.greys.grey2}`,
    },
  }),
  valueContainer: base => ({ ...base, padding: 0 }),
  indicatorSeparator: hiddenIndicatorSeparator,
  dropdownIndicator: (base, { selectProps: { menuIsOpen } }) => ({
    ...base,
    color: theme.palette.brand.primary.black,
    paddingLeft: 0,
    paddingRight: 0,
    transform: menuIsOpen ? "rotate(-180deg)" : "none",
    transition: "transform 0.15s",
  }),
  menu: selectMenuBase,
  menuPortal: selectMenuPortalBase,
  menuList: selectMenuListBase,
  option: (base, state) => ({
    ...selectOptionBase(base, state.isFocused),
    padding: "10px 12px",
    "&:active": {
      background: theme.palette.ui.greys.grey5,
    },
  }),
  placeholder: base => selectPlaceholderBase(base),
};

export const Dropdown = <T extends DropdownValue = DropdownValue>({
  options,
  value,
  onChange,
  placeholder = "Select an option",
  isDisabled,
  isLoading,
  ariaLabel,
}: DropdownProps<T>) => {
  const selectedOption = useMemo(
    () =>
      value == null ? null : (options.find(o => o.value === value) ?? null),
    [options, value],
  );

  const Wrapped = ReactSelect as ReactComponentLike;

  return (
    <Box width="100%">
      <Wrapped
        aria-label={ariaLabel}
        options={options}
        value={selectedOption}
        onChange={(option: DropdownOption<T> | null) =>
          option && onChange(option.value)
        }
        styles={dropdownStyles}
        components={{ Option, SingleValue, DropdownIndicator }}
        placeholder={placeholder}
        isSearchable={false}
        isDisabled={isDisabled || isLoading}
        isLoading={isLoading}
        menuPlacement="auto"
        // Portal the menu to the body so it isn't clipped by parent
        // containers (e.g. Chakra Modal's overflow box).
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : undefined
        }
      />
    </Box>
  );
};
